import "styles/print.css";
import React, { useState, useEffect } from "react";
import ThaiBahtText from "thai-baht-text";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";

function fmt(v) {
  if (v == null) return "0.00";
  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v));
}

function formatThaiDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear() + 543;
  return `${day}/${month}/${year}`;
}

/**
 * แปลง invoice.items เป็น document-level rows
 * แต่ละ invoice item → 1 แถว (เลขที่เอกสาร, วันที่, จำนวนรายการ, มูลค่าก่อนภาษี, ภาษี, สุทธิ)
 */
function mapInvoiceToDocumentRows(invoice) {
  const items = invoice?.items ?? [];

  if (items.length === 0) {
    return [
      {
        documentNumber: invoice?.document_no ?? "-",
        documentDate: "",
        quantity: 1,
        taxable_amount:
          (Number(invoice?.taxable_amount_has_vat) || 0) +
          (Number(invoice?.taxable_amount_zero_vat) || 0) +
          (Number(invoice?.taxable_amount_exempt) || 0),
        vat: Number(invoice?.vat_amount) || 0,
        total_amount: Number(invoice?.grand_total) || 0,
        remark: "",
      },
    ];
  }

  return items.map((item) => {
    const bill = item.memberDebtTransaction?.payment?.bill;
    return {
      documentNumber: bill?.receipt_no ?? "",
      documentDate: bill?.created_at ?? "",
      quantity: item.quantity ?? 1,
      taxable_amount: Number(item.taxable_amount) || 0,
      vat: Number(item.vat) || 0,
      total_amount: Number(item.total_amount) || 0,
      remark: item.remark ?? "",
    };
  });
}

export function PrintRE({ innerRef, receipt, invoice }) {
  const maxRowPerPage = 10;
  const [prow, setProw] = useState([]);
  const [tmpRow, setTmpRow] = useState(0);

  useEffect(() => {
    if (!invoice && !receipt) return;

    const rows = mapInvoiceToDocumentRows(invoice);
    const p = Math.ceil(rows.length / maxRowPerPage) || 1;
    const arr = [];
    for (let i = 1; i <= p; i++) {
      const dataPage = rows.slice((i - 1) * maxRowPerPage, i * maxRowPerPage);
      if (i === p && dataPage.length < maxRowPerPage) {
        setTmpRow(maxRowPerPage - dataPage.length);
      }
      arr.push(dataPage);
    }
    setProw(arr);
  }, [receipt, invoice]);

  const allRows = mapInvoiceToDocumentRows(invoice);
  const totalQuantity = allRows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  const totalTaxable = allRows.reduce((s, r) => s + r.taxable_amount, 0);
  const totalVat = allRows.reduce((s, r) => s + r.vat, 0);
  const totalAmount = allRows.reduce((s, r) => s + r.total_amount, 0);

  return (
    <div ref={innerRef}>
      {prow.map((pageData, index) => (
        <PagePrint
          key={index}
          pageCur={index + 1}
          pageAll={prow.length}
          pdata={pageData}
          tmpRow={tmpRow}
          maxRowPerPage={maxRowPerPage}
          receipt={receipt}
          invoice={invoice}
          totalQuantity={totalQuantity}
          totalTaxable={totalTaxable}
          totalVat={totalVat}
          totalAmount={totalAmount}
        />
      ))}
    </div>
  );
}

const PagePrint = ({
  pageCur,
  pageAll,
  pdata,
  tmpRow,
  maxRowPerPage,
  receipt,
  invoice,
  totalQuantity,
  totalTaxable,
  totalVat,
  totalAmount,
}) => {
  const receiptData = receipt?.memberArPaymentReceipt ?? receipt ?? {};
  const splits = receipt?.splits ?? [];
  const receiptTotal = splits.reduce(
    (sum, s) => sum + (Number(s.amount) || 0),
    0,
  );
  const remainingAmount = Number(receipt?.remaining_amount ?? 0);

  // ─── Business info (dynamic จาก invoice) ──────────────────────
  const business = invoice?.business ?? {};
  const companyName = business.company_name || "-";
  const businessAddress = business.address || "-";
  const businessTaxNo = business.tax_number || "-";
  const businessPhone =
    business.contact_phone_number || business.contact_tel || "-";
  const businessEmail = business.contact_email || "";
  const businessLogo = business.image_path || "/images/logo1.png";

  // ─── Member info ──────────────────────────────────────────────
  const member = invoice?.member ?? {};
  const memberName =
    `${member.first_name_th || ""} ${member.last_name_th || ""}`.trim() ||
    member.company_name_th ||
    member.code ||
    "-";
  const memberAddress = member.address || "-";
  const memberPhone = member.phone_number || "-";
  const memberTaxId = member.tax_number || member.id_card_number || "-";

  // ─── Document dates ───────────────────────────────────────────
  const paymentDate = receipt?.completed_at
    ? formatThaiDate(receipt.completed_at)
    : receiptData?.created_at
      ? formatThaiDate(receiptData.created_at)
      : "-";

  const dueDateStr = invoice?.due_date ? formatThaiDate(invoice.due_date) : "-";

  // ─── Creator ──────────────────────────────────────────────────
  const creator = invoice?.createdByUserBusinessGroup ?? {};
  const creatorName =
    `${creator.first_name || ""} ${creator.last_name || ""}`.trim() || "-";

  // ─── Summary ──────────────────────────────────────────────────
  const grandTotal = Number(invoice?.grand_total) || 0;
  const amountBeforeDiscount = Number(invoice?.amount_before_discount) || 0;
  const discountAmount = Number(invoice?.discount_amount) || 0;
  const amountAfterDiscount = Number(invoice?.amount_after_discount) || 0;
  const vatAmount = Number(invoice?.vat_amount) || 0;
  const taxableHasVat = Number(invoice?.taxable_amount_has_vat) || 0;
  const taxableZero = Number(invoice?.taxable_amount_zero_vat) || 0;
  const taxableExempt = Number(invoice?.taxable_amount_exempt) || 0;
  const hasDiscount = discountAmount > 0;

  // ─── NV/V Breakdown ───────────────────────────────────────────
  const nvTotal = taxableExempt;
  const vTaxExcluded = taxableHasVat + taxableZero;
  const vTotal = taxableHasVat + taxableZero + vatAmount;
  const totalBeforeTax = taxableExempt + taxableHasVat + taxableZero;

  return (
    <div
      className="text-[10.5px] text-[#2C2C2C] mb-1 print-re-page"
      style={{
        width: "210mm",
        height: "297mm",
        padding: "5px 30px",
        margin: "0",
        background: "white",
        position: "relative",
      }}
    >
      {/* ─── Header ─────────────────────────────────────────── */}
      <div className="flex text-[10.5px] items-end mb-3">
        <div className="flex items-start mr-auto mt-3">
          <div className="mr-[15px]">
            <img
              src={businessLogo}
              className="w-[58px] print-logo"
              alt="logo"
            />
          </div>
          <div>
            <div className="font-bold">{companyName}</div>
            <div>{businessAddress}</div>
            <div>เลขผู้เสียภาษี / Tax ID {businessTaxNo}</div>
            <div>
              <span className="mr-2">Tel: {businessPhone}</span>
              {businessEmail && <span>E-mail: {businessEmail}</span>}
            </div>
          </div>
        </div>
        <div>
          <div className="text-right mb-3">
            แผ่นที่ {pageCur}/{pageAll}
          </div>
          <div className="flex items-center">
            {/* <img src="/images/qrcode.png" className="w-[56px] mr-4" alt="qr" /> */}
            <div className="rounded-sm border text-center overflow-hidden">
              <div className="text-[14px] py-1 text-white bg-orange-500">
                ใบเสร็จรับเงิน
              </div>
              <div className="px-2 py-1">
                <span>เลขที่เอกสาร : </span>
                <span className="font-bold">
                  {receiptData?.document_no ?? "-"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Customer & Document info ────────────────────────── */}
      <div className="flex items-end">
        <div className="grow pr-[20px]">
          <div className="flex mb-2">
            <div className="flex-none w-[130px] pr-3">
              ข้อมูลลูกค้า/Customer
            </div>
            <div className="grow text-[#000] font-bold">{memberName}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[130px] text-right pr-3">
              ที่อยู่/Address
            </div>
            <div className="grow text-[#000] font-bold">{memberAddress}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[130px] text-right pr-3">
              เบอร์โทรผู้รับ/Phone
            </div>
            <div className="grow text-[#000] font-bold">{memberPhone}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[130px] text-right pr-3">
              เลขประจำตัวผู้เสียภาษี
            </div>
            <div className="grow text-[#000] font-bold">{memberTaxId}</div>
          </div>
        </div>
        <div className="flex-none mr-4">
          <div className="flex mb-2">
            <div className="flex-none w-[80px] mr-3 text-end">
              วันที่ออกเอกสาร
            </div>
            <div className="grow text-[#000] font-bold">{paymentDate}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[80px] mr-3 text-end">
              วันที่ครบกำหนด
            </div>
            <div className="grow text-[#000] font-bold">{dueDateStr}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[80px] mr-3 text-end">
              เงื่อนไขชำระเงิน
            </div>
            <div className="grow text-[#000] font-bold">
              {invoice?.payment_term_name || "-"}
            </div>
          </div>
        </div>
        <div className="flex-none">
          <div className="flex mb-2">
            <div className="flex-none w-[70px] mr-3">อ้างอิง</div>
            <div className="grow text-[#000] font-bold">
              {invoice?.document_no ?? "-"}
            </div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[70px] mr-3">&nbsp;</div>
            <div className="grow" />
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[70px] mr-3">ผู้จัดทำเอกสาร</div>
            <div className="grow text-[#000] font-bold">{creatorName}</div>
          </div>
        </div>
      </div>

      {/* ─── Items table (document-level) ────────────────────── */}
      <div className="py-2">
        <table className="w-full border-collapse border-slate-700 table-print">
          <thead className="bg-[#FFE5CC]">
            <tr>
              <th
                width="4%"
                className="border-x border-y text-center font-normal"
              >
                ที่
              </th>
              <th
                width="22%"
                className="border-x border-y text-center font-normal"
              >
                เลขที่เอกสาร
              </th>
              <th
                width="14%"
                className="border-x border-y text-center font-normal"
              >
                วันที่เอกสาร
              </th>
              <th
                width="10%"
                className="border-x border-y text-center font-normal"
              >
                จำนวนรายการ
              </th>
              <th
                width="14%"
                className="border-x border-y text-center font-normal"
              >
                มูลค่าก่อนภาษี
              </th>
              <th
                width="12%"
                className="border-x border-y text-center font-normal"
              >
                มูลค่าภาษี
              </th>
              <th
                width="14%"
                className="border-x border-y text-center font-normal"
              >
                มูลค่าสุทธิ
              </th>
              <th
                width="10%"
                className="border-x border-y text-center font-normal"
              >
                หมายเหตุ
              </th>
            </tr>
          </thead>
          <tbody>
            {pdata.map((item, i) => (
              <tr style={{ height: "30px" }} key={i}>
                <td className="border-x ellipsis text-center">
                  {(pageCur - 1) * maxRowPerPage + (i + 1)}
                </td>
                <td className="border-x pl-1 ellipsis">
                  {item.documentNumber}
                </td>
                <td className="border-x text-center ellipsis">
                  {item.documentDate ? formatThaiDate(item.documentDate) : ""}
                </td>
                <td className="border-x text-end pr-1 ellipsis">
                  <NumericFormat
                    displayType="text"
                    thousandSeparator=","
                    decimalScale={0}
                    value={item.quantity}
                  />
                </td>
                <td className="border-x text-end pr-1 ellipsis">
                  {fmt(item.taxable_amount)}
                </td>
                <td className="border-x text-end pr-1 ellipsis">
                  {fmt(item.vat)}
                </td>
                <td className="border-x text-end pr-1 ellipsis">
                  {fmt(item.total_amount)}
                </td>
                <td className="border-x pl-1 ellipsis" style={{ maxWidth: "70px" }}>
                  <div className="text-[9px]">{item.remark}</div>
                </td>
              </tr>
            ))}

            {pageCur === pageAll &&
              [...Array(tmpRow)].map((_, i) => (
                <tr key={`tmp${i}`}>
                  {[...Array(8)].map((_, j) => (
                    <td key={j} className="border-x ellipsis" />
                  ))}
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr style={{ height: "30px" }}>
              <td
                className="border-x border-y text-center font-semibold"
                colSpan={3}
              >
                รวม
              </td>
              <td className="border-x border-y text-end pr-1 font-semibold">
                <NumericFormat
                  displayType="text"
                  thousandSeparator=","
                  decimalScale={0}
                  value={totalQuantity}
                />
              </td>
              <td className="border-x border-y text-end pr-1 font-semibold">
                {fmt(totalTaxable)}
              </td>
              <td className="border-x border-y text-end pr-1 font-semibold">
                {fmt(totalVat)}
              </td>
              <td className="border-x border-y text-end pr-1 font-semibold">
                {fmt(totalAmount)}
              </td>
              <td className="border-x border-y" />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ─── Footer ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2 text-[#000]">
        {/* Left: remark + NV/V breakdown + payment */}
        <div>
          <div className="mb-1">หมายเหตุ</div>
          <div className="rounded-lg border px-4 min-h-[45px] py-1 text-[10px]">
            {receiptData?.remark ?? ""}
          </div>

          {/* NV/V Breakdown */}
          <div className="pt-2">
            <div className="grid grid-cols-4 gap-1 mb-[5px]">
              <div></div>
              <div className="text-end text-[9px]">มูลค่าก่อนภาษี</div>
              <div className="text-end text-[9px]">ภาษีมูลค่าเพิ่ม</div>
              <div className="text-end text-[9px]">ราคารวม VAT</div>
            </div>
            <div className="grid grid-cols-4 gap-1 mb-[5px]">
              <div>NV สินค้าไม่มีภาษี</div>
              <div className="text-end pr-2">{fmt(taxableExempt)}</div>
              <div className="text-end pr-2">{fmt(0)}</div>
              <div className="text-end pr-2">{fmt(nvTotal)}</div>
            </div>
            <div className="grid grid-cols-4 gap-1 mb-[5px]">
              <div>V สินค้าที่มีภาษี 7%</div>
              <div className="text-end pr-2">{fmt(vTaxExcluded)}</div>
              <div className="text-end pr-2">{fmt(vatAmount)}</div>
              <div className="text-end pr-2">{fmt(vTotal)}</div>
            </div>
            <div className="grid grid-cols-4 gap-1 mb-[5px] font-semibold">
              <div>มูลค่าสินค้ารวม</div>
              <div className="text-end pr-2">{fmt(totalBeforeTax)}</div>
              <div className="text-end pr-2">{fmt(vatAmount)}</div>
              <div className="text-end pr-2">{fmt(grandTotal)}</div>
            </div>
          </div>

          {/* Payment info */}
          <div className="pt-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[11px] font-semibold mb-2">ชำระเงิน</div>
                <div className="px-1">
                  <div className="mb-1">
                    <span className="inline-block w-[85px]">
                      วันที่ชำระเงิน
                    </span>
                    <span>{paymentDate}</span>
                  </div>
                  <div className="mb-1">
                    <span className="inline-block w-[85px]">จำนวนเงินรวม</span>
                    <span className="font-semibold">
                      {fmt(receiptTotal)} บาท
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold mb-2">
                  วิธีการชำระเงิน
                </div>
                <div>
                  {splits.map((split, idx) => {
                    const bank = split?.branchBankSetting;
                    return (
                      <div key={idx} className="mb-2">
                        <div className="flex items-center gap-1">
                          <div className="flex-1">
                            {split?.paymentMethod?.name ?? "-"}
                          </div>
                          <div className="text-right font-semibold">
                            {fmt(split.amount)} บาท
                          </div>
                        </div>
                        {bank && (
                          <div className="text-[8.5px] pl-2 mt-[1px] text-[#2C2C2C] leading-[1.3]">
                            <div>
                              {bank.bank?.name ?? "-"}
                              {bank.branch_name ? ` (${bank.branch_name})` : ""}
                            </div>
                            <div>{bank.account_number}</div>
                            <div>{bank.account_name}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Right: summary */}
        <div className="pl-[30px]">
          {hasDiscount && (
            <>
              <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
                <div className="col-span-7">มูลค่าสินค้ารวมภาษี</div>
                <div className="col-span-3">{fmt(amountBeforeDiscount)}</div>
                <div className="col-span-2">บาท</div>
              </div>
              <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
                <div className="col-span-7">ส่วนลด</div>
                <div className="col-span-3">{fmt(discountAmount)}</div>
                <div className="col-span-2">บาท</div>
              </div>
              <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
                <div className="col-span-7">มูลค่าสินค้าหลังหักส่วนลด</div>
                <div className="col-span-3">{fmt(amountAfterDiscount)}</div>
                <div className="col-span-2">บาท</div>
              </div>
              <div className="py-1">
                <hr />
              </div>
            </>
          )}

          {taxableExempt > 0 && (
            <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
              <div className="col-span-7">มูลค่าสินค้าไม่มีภาษี</div>
              <div className="col-span-3">{fmt(taxableExempt)}</div>
              <div className="col-span-2">บาท</div>
            </div>
          )}

          {taxableZero > 0 && (
            <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
              <div className="col-span-7">มูลค่าสินค้า VAT 0%</div>
              <div className="col-span-3">{fmt(taxableZero)}</div>
              <div className="col-span-2">บาท</div>
            </div>
          )}

          <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
            <div className="col-span-7">มูลค่าสินค้าก่อนภาษี</div>
            <div className="col-span-3">{fmt(taxableHasVat)}</div>
            <div className="col-span-2">บาท</div>
          </div>

          <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
            <div className="col-span-7">ภาษีมูลค่าเพิ่ม/VAT</div>
            <div className="col-span-3">{fmt(vatAmount)}</div>
            <div className="col-span-2">บาท</div>
          </div>

          <div className="py-2 bg-[#fceadc] text-[11.5px] mb-2">
            <div className="grid grid-cols-12 gap-2 text-end">
              <div className="col-span-7">มูลค่ารวมสุทธิ/NET AMOUNT/VAT</div>
              <div className="col-span-3 font-bold">{fmt(grandTotal)}</div>
              <div className="col-span-2">บาท</div>
            </div>
          </div>

          <div className="text-end text-[11.5px] text-[#000] font-semibold">
            ( {ThaiBahtText(String(grandTotal.toFixed(2)))} )
          </div>

          {/* ─── Partial payment notice ───────────────────────── */}
          {remainingAmount > 0 && (
            <div className="text-end text-[11.5px] text-[#000] font-semibold mt-1">
              ชำระเงินบางส่วน {fmt(receiptTotal)} บาท
              <br />
              รวมชำระ {fmt(grandTotal - remainingAmount)} บาท
              <br />
              ค้างชำระอีก {fmt(remainingAmount)} บาท
            </div>
          )}

          {/* ─── Signatures ───────────────────────────────────── */}
          <div className="py-2 text-[11.5px] pt-5 mb-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center justify-center flex-col">
                <div className="h-[20px] border-b w-[100px] border-dashed border-black mb-2" />
                <div className="mb-2">วันที่ _____/_____/_____</div>
                <div>ผู้จัดทำ</div>
              </div>
              <div className="flex items-center justify-center flex-col">
                <div className="h-[20px] border-b w-[100px] border-dashed border-black mb-2" />
                <div className="mb-2">วันที่ _____/_____/_____</div>
                <div>ผู้อนุมัติ</div>
              </div>
              <div className="flex items-center justify-center flex-col">
                <div className="h-[20px] border-b w-[100px] border-dashed border-black mb-2" />
                <div className="mb-2">วันที่ _____/_____/_____</div>
                <div>ลูกค้า</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
